export type HotkeyDefinition = {
  id: string;
  /** Key combo passed to react-hotkeys-hook, such as "mod+k" or "g>c". */
  keys: string;
  /** Human-readable key combination shown in shortcut help. */
  display: string;
  description: string;
  group: string;
};

export type HotkeyHandler = () => void | Promise<void>;

type RegisteredHotkey = {
  definition: HotkeyDefinition;
  handler?: HotkeyHandler;
};

const registry = new Map<string, RegisteredHotkey>();

export function registerHotkey(
  definition: HotkeyDefinition,
  handler?: HotkeyHandler,
): void {
  if (registry.has(definition.id)) {
    throw new Error(`Hotkey "${definition.id}" is already registered`);
  }

  registry.set(definition.id, { definition, handler });
}

export function unregisterHotkey(id: string): void {
  registry.delete(id);
}

export function getHotkeys(): HotkeyDefinition[] {
  return Array.from(registry.values(), ({ definition }) => definition);
}

export async function executeHotkey(id: string): Promise<boolean> {
  const handler = registry.get(id)?.handler;
  if (!handler) {
    return false;
  }

  await handler();
  return true;
}

export function getHotkeysByGroup(): Record<string, HotkeyDefinition[]> {
  return getHotkeys().reduce<Record<string, HotkeyDefinition[]>>(
    (hotkeysByGroup, hotkey) => {
      (hotkeysByGroup[hotkey.group] ??= []).push(hotkey);
      return hotkeysByGroup;
    },
    {},
  );
}

/** Reset the module singleton between tests. */
export function clearRegistry(): void {
  registry.clear();
}
